import { computeClassSchema, sizeSchema } from "./compute-class.js";
import "./dp-sizing.js";
import { dpAdminLimitsSchema } from "./dp-admin-limits.js";
import { unsatisfiedDemandSchema } from "./unsatisfied-demand.js";
import { z } from "zod";
//#region src/dp-compute-view.ts
/** Read-only base-pool disk display default (GiB). Base-pool disk is not a per-DP knob this PR (§13.1). */
const COMPUTE_VIEW_DEFAULT_DISK_GIB = 100;
/** Fat-finger upper guard on the DISPLAYED pool disk (fail-closed hygiene; disk is read-only-derived). */
const COMPUTE_VIEW_DISK_GIB_MAX = 65536;
/**
* The pool roles the view enumerates (DEC-CL-a). Layer-A = 'infra' | 'default-app' (editable sizing);
* Layer-B = 'demand' | 'dedicated' (read-only posture — DEC-COMPUTE-WIRE). provisioning-svc emits NO
* 'dedicated' pool this PR (§13.0 — that data lives in release-engine's dp_live_status and is not read
* here); the role is in the enum so WS-6's read-only dedicated card (RW2-6-6) and any later producer bind
* to this contract without a wire change.
*/
const poolComputeRoleSchema = z.enum([
	"infra",
	"default-app",
	"demand",
	"dedicated",
	"app-pool"
]);
/** The Layer-A pool keys — the ONLY keys the PATCH `pools` object accepts (DEC-COMPUTE-WIRE). */
const layerAPoolKeySchema = z.enum(["infra", "default-app"]);
/**
* SM-6 advisory demand-check inputs (per pool). ALL approximate — WS-6 labels them advisory (DEC-CL-d) and
* NEVER sizes from them (D3). node-capacity + price are the reachable inputs; the fixture-demand inputs
* (`declaredPeakReplicas`/`perReplica*`) are a documented 0-baseline this PR (DEC-SM6-ADVISORY / plan §12
* escalation #4). `0` means "no data" — the consumer renders "—" (can't estimate), never "0 nodes".
*
* # ⛔ PROVENANCE OF `nodeCpuMilli` / `nodeMemMiB` — READ THIS BEFORE COMPUTING A PERCENTAGE
*
* They are the machine type's **DERIVED ALLOCATABLE ceilings** from the CP's region catalog
* (provisioning-svc `lib/machine-type-catalog/allocatable.ts`): the real observed kubelet allocatable
* where the fleet has run that shape, and GKE's published node-reservation rules modelled from the SKU
* everywhere else. They are **NOT the nominal SKU numbers**, and they are **NOT node capacity** — a
* 4-vCPU / 16 GiB `e2-standard-4` reports `3920` / `13273` where the fleet has run the shape, or
* `3920` / `13621` modelled where it has not, and **never** `4000` / `16384`.
*
* ⚠️ **BOTH derived memory values are expected; neither is a bug.** Which one you see depends only on
* whether some data plane in that region has actually run the shape, so the same field legitimately
* reads `13273` in one region and `13621` in another. ⛔ Do not file the modelled value as a defect
* against the measured one — the CPU ceiling is `3920` either way, and it is only memory that differs
* (see the upper-bound note below for why).
*
* ⚠️ **They used to be nominal, and it was the same denominator problem twice.** Until 2026-08-14 the
* catalog published `guestCpus * 1000` / `memoryMb` verbatim, so this advisory disagreed with
* `GET /v1/admin/data-planes/:id/utilisation` — whose denominator has always been live allocatable — by
* **3111 MiB on a single `e2-standard-4`, i.e. the catalog was 23% high relative to reality**, or about
* **42.5 GiB of phantom headroom across a 14-node data plane**. A caller dividing usage by `nodeMemMiB`
* UNDERSTATED utilisation, which is the false-reassurance direction. Both surfaces now derive from the
* same allocatable numbers; do not reintroduce a second provenance.
*
* ⚠️ **The modelled arm is an UPPER BOUND, not a floor.** GKE computes its reservations from node
* CAPACITY, but GCP discovery exposes only the machine type's NOMINAL memory, and the ~2.4% firmware/
* kernel gap between them is not published. A modelled `nodeMemMiB` therefore sits up to ~2.6% above
* true allocatable until some data plane in that region actually runs the shape, at which point the
* measured value replaces it exactly. Round DOWN when a decision depends on the margin.
*/
const poolAdvisorySchema = z.object({
	declaredPeakReplicas: z.number().int().nonnegative(),
	perReplicaCpuMilli: z.number().int().nonnegative(),
	perReplicaMemMiB: z.number().int().nonnegative(),
	nodeCpuMilli: z.number().int().nonnegative(),
	nodeMemMiB: z.number().int().nonnegative(),
	pricePerHourUsd: z.number().nonnegative().optional()
}).strict();
/**
* One pool's compute posture in the GET view. `.strict()`. There is **NO per-pool `spot`** and **no per-pool
* cap** (DEC-COMPUTE-WIRE) — spot and the cap are DP-wide, on the root `limits`.
* `minNodes`/`maxNodes`/`keepWarm`/`diskSizeGb` are: Layer-A = the pool's real sizing (editable via PATCH);
* Layer-B = a read-only posture display of the pool's REAL applied bounds (demand: minNodes 0, maxNodes = the
* platform default baked into the composition — 3 for GPU, else 10; keepWarm 0) — never patchable per-pool.
* The DP-wide `limits.maxNodesPerPool` is NOT the demand pools' applied bound (DEC-MAXNODES-DEMAND-DEFER: it
* governs the DEDICATED pools WS-3 compiles this PR) — do not render it as one.
* `key`/`layer`/`title` are NOT on the wire: WS-6 derives them from `role` + `nodeType`.
*/
const poolComputeViewSchema = z.object({
	role: poolComputeRoleSchema,
	nodeType: z.string().min(1),
	stateful: z.boolean(),
	minNodes: z.number().int().min(0).max(100),
	maxNodes: z.number().int().min(1).max(100),
	keepWarm: z.number().int().min(0).max(100),
	diskSizeGb: z.number().int().min(1).max(COMPUTE_VIEW_DISK_GIB_MAX),
	liveNodeCount: z.number().int().nonnegative().nullable(),
	/**
	* D16 (R-11/F14) — the subset of {@link liveNodeCount} whose `Ready=True`.
	*
	* ⛔⛔ THE COLUMN THE CONSOLE HEADS "Live" HAS BEEN SHOWING THE TOTAL. `readyNodeCount` has been
	* produced by the operator, persisted by release-engine and refine-guarded
	* (`readyNodeCount <= nodeCount`, `@zion/sdk-cp-dp-contracts/live-node-pool.ts:146`) since R8-1,
	* and read by **NOBODY** — so a pool running 5 nodes with 0 Ready rendered `Live: 5`. It was
	* harmless on `dp-app` only because all 14 nodes were Ready, which is exactly why it went
	* unnoticed. `DataPlaneCapacityTab.tsx:321-330` is the reader that makes this field worth adding.
	*
	* ⛔⛔ THE TWO SIBLINGS SAY "NOT COUNTED" IN **DIFFERENT** WAYS, AND THIS IS THE ONE THING A
	* CONSUMER MUST NOT ASSUME AWAY. `liveNodeCount` is required-and-nullable, so it says it with
	* **`null`**. This field is `.optional()`, so it says it with an **ABSENT KEY** — and, for a row
	* that carries it, also with `null`. ⇒ **The not-counted state is `liveNodeCount === null` together
	* with `readyNodeCount` being absent OR null.** ⛔ Do NOT write "both are `null`": an earlier draft
	* of this docblock promised *"every count is `null` together … a consumer may rely on that
	* pairing"*, which is FALSE under `.optional()` and is exactly the sentence a consumer writes
	* `?? 0` against — turning "we could not count" into "zero nodes are Ready" on a healthy pool.
	* ⇒ Treat **absent and `null` as the same fact** here, and treat asymmetry with `liveNodeCount` as
	* expected rather than as a bug to normalise. WS-2 pins the real behaviour with a test asserting
	* `liveNodeCount === null` **and** `'readyNodeCount' in pool === false` in one case.
	* ⚠️ What DOES still hold: a row never reports a Ready count for a census it could not take. So a
	* NUMBER here implies `liveNodeCount` is a number too — the implication runs one way only.
	* ⚠️ A measured `0` is an ANSWER — nodes exist, none Ready — and must render as such; it is NOT the
	* not-counted state.
	*
	* ⚠️ `.nullable().optional()` (R-36/R-39). ⛔ The reason is INSTRUMENT INTEGRITY, not convenience:
	* required would break the `PoolComputeView` construction sites in two OTHER packages, leaving
	* `cp-admin-ui` typecheck red across several stages — and **a gate red for expected reasons cannot
	* detect an unexpected one.** This repo has form: §3G gate 8 sat red on `main` for one false
	* positive while nothing ran it, and CLAUDE.md draws the lesson that *"a permanently-red one trains
	* readers to skip its output."* ⭐ And absence costs nothing here: for a COUNT, *absent* and
	* *`null`* both mean **not counted**, so the distinction is not load-bearing. ⚠️ Contrast
	* {@link dpComputeViewSchema.shape.unsatisfiedDemand}, where absent-vs-`[]` IS load-bearing and
	* `.optional()` is what PRESERVES it.
	*
	* ⛔ NEVER SUM THESE FOR A FLEET FIGURE (R-11.5). The census's `unattributed` row belongs to no
	* pool — on `dp-app` that is **5 of 14 nodes** — so `sum(pools[].readyNodeCount)` is wrong by
	* construction. ⚠️ A census-wide Ready pair on `liveNodeTotalsSchema` was specified and then
	* **DROPPED (R-41)** because nothing consumed it; do not re-add it without a named reader.
	*/
	readyNodeCount: z.number().int().nonnegative().nullable().optional(),
	/**
	* The per-pool fragmentation scalars from §1, PROJECTED VERBATIM from the DP's census
	* (C-043). Same names, same units, same meaning as
	* `@zion/sdk-cp-dp-contracts/live-node-pool.ts` — see `contracts.md` §1 for the four
	* ways to compute them wrong, all of which are the PRODUCER's problem and none of which
	* this projection may re-litigate.
	*
	* ⛔⛔ COPY, NEVER RE-DERIVE, AND NEVER DEFAULT. This projection reads the value off the
	* joined live-pool row and passes it through. It does not compute, clamp, sum, or
	* substitute. ⛔ No `?? 0` and no `?? null`: a zero here is a MEASURED ANSWER (the
	* fullest node in this row has no room), so a fallback converts "we could not measure"
	* into "there is no space", which is the one direction that reads as alarming rather than
	* reassuring and will be believed.
	*
	* ⛔ ABSENCE CONVENTION — STATED, NOT INHERITED (C-011). This member is `.optional()` and
	* **NOT `.nullable()`**, which is neither sibling's convention:
	*
	*   | state | encoding | means |
	*   |---|---|---|
	*   | a number, including `0` | present | MEASURED. A real answer. `0` = no room on the fullest node. |
	*   | key **ABSENT** | — | NOT MEASURED. |
	*   | `null` | ⛔ unrepresentable | — |
	*
	* ⇒ **There is exactly ONE not-measured encoding, and that is deliberate.** §1 declares
	* these `.optional()` with no `.nullable()`, so the wire cannot produce a `null` — and
	* adding `.nullable()` here would create a fourth state no producer can emit, i.e. a
	* branch a test can only reach by constructing input the wire forbids. A tolerance nothing
	* can produce is a fail-open branch, not defensive coding.
	*
	* ⛔ DO NOT COPY `liveNodeCount`'s required-and-nullable form, and DO NOT COPY
	* `readyNodeCount`'s `.nullable().optional()` form. Both are on this same object, both are
	* counts, and they disagree with each other for reasons recorded in their own docblocks.
	* Neither reason applies to a scalar: a count has a "we looked and there are none" answer
	* that is spelled `0`, and so does this — the asymmetry those two carry is about the
	* NUMBER's own absence, which §1 already resolves at the producer with the
	* all-or-nothing rule.
	*
	* ⚠️ TWO CAUSES OF ABSENCE, AND THEY ARE DISCRIMINATED BY `liveNodeCount`, NOT BY A
	* SECOND ENCODING. Absent means either (a) the row exists and §1's all-or-nothing rule
	* suppressed the pair — an old DP, or a node whose allocatable or pod list did not parse
	* — or (b) there is no census row for this pool at all. **Read `liveNodeCount` to tell
	* them apart:** `liveNodeCount === null` is (b); a number is (a). ⚠️ The implication runs
	* ONE WAY, exactly as `readyNodeCount`'s docblock records for itself: a NUMBER here
	* implies `liveNodeCount` is a number too, and the converse does not hold. Do not add a
	* `.refine()` pinning the pairing — it is not a pairing.
	*
	* ⛔ ALL-OR-NOTHING SURVIVES THE HOP. §1 emits both scalars or neither, per row, and the
	* projection is per-key, so the property is preserved rather than re-enforced. ⇒ **Do NOT
	* write a `.refine()` asserting the two are present together.** It would pass on every
	* input the wire can produce, which makes it a test of §1's producer running in the wrong
	* package — and a refine that cannot fail is the vacuous-assertion shape this plan-set
	* bans everywhere else.
	*
	* ⛔ THESE AGE, SO THEY BLANK WITH THE CENSUS (C-052) — this is the one behavioural rule
	* a consumer must know and it does NOT live in this file. A free-space maximum is an
	* OBSERVATION of committed-versus-allocatable at an instant, not a SKU property, so it
	* ages exactly as `liveNodeCount` does. ⇒ A stale census blanks these two **and** the
	* counts, while the bounds on `dpComputeViewSchema` stay populated because they are a live
	* CP read (§4, C-035). WS-4 owns the blanking; this member's contract is only that it is
	* blankable, i.e. `.optional()` is load-bearing for that too.
	*
	* ⛔⛔ ON A ROLE-MATCHED ROW THIS IS A **LOWER BOUND**, NEVER THE VALUE ITSELF (C-089). A view row
	* for `infra` or `default-app` matches census rows **by role alone**, so it can span several node
	* types — deliberately, so a reroll's surge pool is counted rather than dropped
	* (`live-node-join.ts:155-159`). The projection takes the **`Math.max` over the matching rows that
	* REPORTED**, and is absent only if none did.
	*
	* ⇒ **So the honest reading is: "at least this much free on some single node in this pool."** If a
	* matching row did not report, the true largest gap may be LARGER. It is never smaller.
	*
	* ⭐ WHY THAT IS THE RIGHT SHAPE RATHER THAN A COMPROMISE. For the question this field exists to
	* answer — *can a pod of size R land?* — a lower bound never produces a false YES. If the value is
	* `≥ R`, a real node genuinely has that gap and the pod fits. If it is `< R`, the answer may be a
	* false NO. **A false "will not fit" costs an operator one extra look; a false "will fit" is the
	* 4.4-hour rollout deadlock this feature exists to prevent.**
	*
	* ⛔ AND DO NOT "CORRECT" THIS TO ALL-OR-NOTHING BY ANALOGY WITH §1. §1's rule is about a **sum**
	* WITHIN a row, where a partial sum understates commitment and thus OVERSTATES free space —
	* reassuring and unsafe. This is a **max** ACROSS rows, where a subset UNDERSTATES free space —
	* alarming and safe. **The rule was never "be all-or-nothing"; it was "never err reassuring."**
	*
	* ⛔ NEVER `+=`. `liveNodeCount` on the same row IS a sum (`live-node-join.ts:128`), so summing is
	* exactly what a reader who pattern-matches the neighbour will write, and §1 bans it: a sum of
	* per-node maxima describes no node that exists.
	*
	* ⚠️ NOT A "WILL IT FIT" PREDICATE, unchanged from §1: taints, tolerations and affinity are
	* not modelled and every `default-app` node carries `zion.witness.ai/pool:NoSchedule`. ⇒ Combined
	* with the lower-bound property above, this value is **neither an upper nor a lower bound on
	* schedulability** — it bounds the OBSERVED GAP only, and taints can still refuse a pod that fits.
	*/
	maxFreeCpuMilliOnAnyNode: z.number().int().nonnegative().optional(),
	/** The same maximum for memory, in BYTES. Every rule above applies unchanged (§1). */
	maxFreeMemoryBytesOnAnyNode: z.number().int().nonnegative().optional(),
	advisory: poolAdvisorySchema
}).strict();
/**
* The aggregated GET /compute response — the CANONICAL wire WS-6 imports (DEC-COMPUTE-WIRE). `.strict()`.
* `limits` is the DP-wide `dpAdminLimitsSchema` object at TOP LEVEL, mirroring the single `dp_admin_limits`
* row VERBATIM (DEC-SM2-c): every field OPTIONAL, `{}` when there is no row. Absent-field meaning (DEC-D10 /
* DEC-ABSENT-DEFAULTS): no `maxNodesPerPool` = no ADMIN cap (each pool keeps its platform default — NOT
* "unbounded"); no `allowDedicated` = dedicated allowed; no `spotPolicy` = 'forbid' (fail-closed);
* absent-or-null `spendCeilingUsdPerHour` = no (advisory) ceiling.
*/
/**
* R8-1 — the nodes the LIVE census counted that **no pool in `pools` claims**.
*
* ⛔⛔ **THIS EXISTS SO THE FLEET TOTAL CANNOT LIE.** `pools` is a projection of what the CP *intends*
* a data plane to have; the census is a measurement of what it *is*, and the two are allowed to
* disagree. Measured on the live fleet 2026-08-07: **3 of 12** real nodes sat in the legacy conflated
* `{xr}-node-pool`, which this view does not model at all. Summing `pools[].liveNodeCount` alone
* therefore yields a *complete-looking* **9** — the same confident-wrong-number defect as the Crossplane
* `atProvider` echo this whole feature replaces, arriving through arithmetic instead of through a field.
*
* ⇒ `sum(pools[].liveNodeCount) + unattributedNodeCount === totalNodeCount`, by construction.
* ⛔ A consumer that renders a node total MUST render this alongside it, named. It is not a remainder to
* fold in silently, and it is not an error state — dedicated pools and a retired-but-populated machine
* type land here legitimately.
*
* ⚠️ `null` on every field together = **not counted** (an operator too old to report a census, a
* k3d/local DP that has no node pools to count, a failed node list, or a DP that has never reported).
* ⛔ Never zero. `0` means the census ran and everything it saw was attributable.
*/
const liveNodeTotalsSchema = z.object({
	unattributedNodeCount: z.number().int().nonnegative().nullable(),
	unpricedNodeGroupCount: z.number().int().min(0).nullable().optional(),
	totalNodeCount: z.number().int().nonnegative().nullable()
}).strict();
/**
* A node pool's autoscaler bounds and zone span, as DESIRED STATE on the Crossplane NodePool MR.
*
* ⛔ READ `spec.forProvider`, NEVER `status.atProvider`. MEASURED across all five MRs on
* `zion-cp-dev` 2026-08-31: `spec.forProvider.nodeLocations` agreed with `gcloud locations[]` 5 of 5,
* including the demand pool's 4, while `status.atProvider.nodeCount` was wrong 5 of 5 (reporting 3
* where 10 nodes ran, 0 where 2 ran). Two fields on ONE object with opposite reliability.
*
* ⚠️ The 2026-08-07 ban on `atProvider` as a capacity source is FIELD-SCOPED. Reading it as
* "distrust the NodePool MR" throws away the one field that makes the effective ceiling computable.
*
* The container of `spec.forProvider.autoscaling` depends on the API version of the read (C-024).
* On `container.gcp.upbound.io/v1beta2` it is an object. On `v1beta1` it is a single-element array.
* Every NodePool that the compositions chart renders is `v1beta2`: the fragments
* `_base-node-pools.gotmpl`, `_machine-type-node-pools.gotmpl`, `_app-pool-node-groups.gotmpl`,
* `_satellite-gcp.gotmpl` and `cloud-gcp.yaml`, and `gcp-api-versions.spec.ts` holds them to it. A
* ref on an XR that the chart rendered before crossplane-upgrade stage 5 still names `v1beta1`. On
* that read `autoscaling.maxNodeCount` is `undefined`, and a reader that reads the key directly
* classifies the pool as `fixed`. That is the R2-1 collapse through the container type.
*
* So the reader unwraps the singleton container and accepts both the `v1beta1` array and the
* `v1beta2` object. `boundsOfNodePoolMr` in provisioning-svc's `node-pool-bounds.ts` does this.
*
* ⛔ **Unwrapping the CONTAINER is NOT normalising the KEY SETS.** C-018 forbids merging
* `maxNodeCount` with `totalMaxNodeCount`; it says nothing about the array wrapper. A developer
* handed "do not normalise" will otherwise refuse to unwrap. Both clauses, always adjacent.
*
* ⚠️ The lead published the unwrapped form in C-013 and C-018 without knowing it: the probe that
* measured it contained `au[0] if isinstance(au, list)`. **A convenience in an instrument became a
* claim about the subject.**
*/
const poolAutoscalerBoundsSchema = z.discriminatedUnion("boundsKind", [
	z.object({ boundsKind: z.literal("fixed") }).strict(),
	z.object({
		boundsKind: z.literal("per-zone"),
		minNodesPerZone: z.number().int().nonnegative(),
		maxNodesPerZone: z.number().int().nonnegative(),
		/**
		* `spec.forProvider.nodeLocations.length` — the zone count this bound is multiplied by.
		*
		* ⛔ REQUIRED ON THIS ARM ONLY (C-026). A per-zone bound without its multiplier IS the
		* 3-to-18 trap, so this arm fails closed without it. `cluster-total` does not need it, and
		* requiring it there made a readable isolated-pool total unreportable whenever
		* `nodeLocations` had not late-initialised.
		*
		* ⛔ NOT the count of zones currently HOLDING a node. MEASURED: counting distinct
		* `topology.kubernetes.io/zone` labels on live nodes is wrong on 2 of 5 pools
		* (`e2-standard-4` configured 4 observed 2; `iso-...` configured 3 observed 1). And
		* `default-app` is one of the three where the naive count is exactly right, so an
		* implementation validated there passes, ships, and is wrong by 2x on a demand pool.
		*
		* ⚠️ `nodeLocations` is genuinely absent sometimes: the base fragment renders no key at all
		* (it arrives by Crossplane late-init) and the demand fragment renders it conditionally
		* (`_machine-type-node-pools.gotmpl:72-74`).
		*/
		configuredZoneCount: z.number().int().positive()
	}).strict(),
	z.object({
		boundsKind: z.literal("cluster-total"),
		minNodesClusterTotal: z.number().int().nonnegative(),
		maxNodesClusterTotal: z.number().int().nonnegative()
	}).strict()
]).and(z.object({ 
/**
* `spec.forProvider.autoscaling.locationPolicy`, carried verbatim for display.
*
* ⚠️ NOT MODELLED. It appears on 3 of 5 pools as `ANY` and affects where the autoscaler
* places nodes. It may explain a pool observed with 4 nodes in one zone against a per-zone
* bound of 3. Nothing computes with this field; it exists so an operator reading a surprising
* distribution sees why it might be surprising.
*/
locationPolicy: z.string().min(1).nullable() }).strict());
/**
* One NodePool MR's entry in the collection. ▸ NEW (C-027).
*
* ⛔ `bounds` NEEDS ITS OWN `null`, AND THIS IS THE LOAD-BEARING PART.
* `getCrossplaneSnapshot` (`provisioning-runtime.impl.ts:234`, inside the `catch` opening at `:230`)
* pushes a **status-less,
* spec-less STUB** `{apiVersion, kind, metadata:{name}}` for any MR it could not GET. A reader that
* classifies "no `autoscaling` block" as `boundsKind: 'fixed'` therefore reports **an UNREADABLE
* pool as a FIXED-SIZE pool** — the R2-1 rendering collapse C-011 defends against, reintroduced one
* level out from where §2 defends it. `bounds: null` keeps that fourth fact where it belongs and
* leaves §2 at three arms.
*
* ⛔ `gkeNodePoolId` is nullable for the same reason: the stub carries no annotations, and deriving
* the id from `metadata.name` is FAIL-WRONG — `dp-app-infra-e2-standard-4` is not
* `infra-e2-standard-4`.
*/
const nodePoolBoundsEntrySchema = z.object({
	mrName: z.string().min(1),
	gkeNodePoolId: z.string().min(1).nullable(),
	bounds: poolAutoscalerBoundsSchema.nullable()
}).strict();
/**
* The EFFECTIVE ceiling: the number of nodes this pool may actually reach.
*
* `per-zone`      -> `maxNodesPerZone * configuredZoneCount`
* `cluster-total` -> `maxNodesClusterTotal` (already a total; DO NOT multiply)
* `fixed`         -> `null`, meaning the pool does not autoscale
*
* ⛔ THIS IS THE ONLY NUMBER AN MCP TOOL PRESENTS AS "THE MAXIMUM". The per-zone bound is shown only
* as the derivation beside it, never alone (C-016).
*/
function effectiveMaxNodesTotal(b) {
	switch (b.boundsKind) {
		case "fixed": return null;
		case "per-zone": return b.maxNodesPerZone * b.configuredZoneCount;
		case "cluster-total": return b.maxNodesClusterTotal;
	}
}
const dpComputeViewSchema = z.object({
	pools: z.array(poolComputeViewSchema),
	limits: dpAdminLimitsSchema,
	/**
	* R8-1 — the census roll-up for this data plane. OPTIONAL/additive: a CP that predates the field
	* omits it, and a consumer that has not adopted it is unaffected. See {@link liveNodeTotalsSchema}
	* for why a node total rendered WITHOUT it is a wrong number rather than a rounded one.
	*/
	liveNodeTotals: liveNodeTotalsSchema.optional(),
	/**
	* C-018 / C-027. The CP-side read of each NodePool MR's autoscaler bounds and zone span.
	*
	* ⛔ TOP-LEVEL, NOT PER POOL ROW, AND THE KEYING IS THE REASON. `pools` is one row per
	* `(role, nodeType)`; a GKE node pool is one pool. MEASURED 2026-08-31 on `dp-app`: of the five
	* live NodePool MRs, the isolated pool and the legacy conflated `{xr}-node-pool` have NO row in
	* `pools` at all, and {@link liveNodeTotalsSchema} already records the legacy one holding 3 of
	* 12 real nodes. A per-row carrier would drop both silently. This member sits beside
	* `liveNodeTotals` and `unsatisfiedDemand` for the same reason they exist: `pools` is a
	* projection of what the CP intends, and these are measurements of what exists.
	*
	* ⇒ A consumer joins `gkeNodePoolId` against a node's `cloud.google.com/gke-nodepool` label, or
	* matches `mrName` by prefix. It must NOT assume every entry corresponds to a `pools` row.
	*
	* ⛔ THREE STATES, ENCODED POSITIONALLY (C-011), and the first is the ABSENCE OF THE KEY:
	*  - **absent** ⇒ the bounds were not read at all: no DataPlane XR exists yet, or the snapshot
	*    read failed.
	*  - **`[]`** ⇒ read successfully, and this data plane has no GKE node pools. A REAL ANSWER, and
	*    the honest one for a local/k3d data plane, where the composition renders no NodePool MR
	*    (`fragments/base-node-pools.yaml:2`, `provider: gcp` gated).
	*  - **entries** ⇒ one per NodePool MR, each carrying its own bounds or its own `null`.
	* ⛔ Do NOT `.default([])`. That collapses "we never looked" into "there are none", which is the
	* defect this convention exists to prevent, and it is the same argument the sibling
	* {@link dpComputeViewSchema.shape.unsatisfiedDemand} member makes.
	*
	* ⚠️ NOT SUBJECT TO THE D17 STALENESS GATE, and this is deliberate. These are DESIRED STATE read
	* live from the CP's own API server at request time, not a point-in-time census carried on a
	* last-write row from a data plane. The distinction is the one this file already draws for the
	* allocatable ceilings (R-9: freshness governs a count, never a ceiling). A consumer must not
	* wrap these in the staleness envelope, which covers DP-REPORTED numbers only (C-035).
	*/
	poolAutoscalerBounds: z.array(nodePoolBoundsEntrySchema).optional(),
	/**
	* D19 (R-42) — the console-facing half of `StatusReport.unsatisfiedDemand`: machine types this data
	* plane could not satisfy. **Keyed by `nodeType`, TOP-LEVEL, one carrier per layer.**
	*
	* ⛔⛔ THIS IS WHY `liveNodeCount: 0` STOPS BEING TWO FACTS. A `demand` pool at `0` is a genuine
	* scale-to-zero *unless* its `nodeType` appears here, in which case it is the D12 failure. Before
	* this field the two were byte-identical on the wire and **no UI change could separate them**.
	* ⇒ A consumer joins `pools[].nodeType` against these entries. It must NOT try to read the
	* distinction out of the pool row alone; there is nothing there to read.
	*
	* ⛔ THREE STATES, ENCODED POSITIONALLY — and this is why there is no per-pool discriminated union
	* (R-42 deleted one that had zero consumers):
	*  - **absent** ⇒ NOT MEASURED (a CP predating the field, a DP that never reported, a failed read).
	*  - **`[]`** ⇒ *we looked, and nothing is unsatisfied.* A REAL ANSWER, and not the same as absent.
	*  - **entries** ⇒ these node types could not be satisfied.
	* ⛔ `.optional()` is what makes absent-vs-`[]` expressible, so it is load-bearing here rather than
	* a back-compat convenience. **Do NOT `.default([])`** — that collapses two states into one and
	* renders "nothing is wrong" for a data plane nobody asked.
	*
	* ⚠️ Same SEMANTIC as the wire field, verbatim: *demand the data plane could not satisfy at
	* observation time — **includes node types that have live nodes but still have unschedulable
	* pods***. ⛔ NOT "never obtained a node" — a type with four healthy nodes and one quota-blocked pod
	* belongs here, and a consumer that renders these as "never got a node" is wrong for that case.
	* ⇒ `kind`-free by design: the sentence a UI renders needs the entry **plus** that type's live-node
	* count, never the entry alone.
	*
	* ⚠️ Bounded by {@link UNSATISFIED_DEMAND_MAX}, mirroring the wire cap so the two cannot disagree.
	*/
	unsatisfiedDemand: z.array(unsatisfiedDemandSchema).max(200).optional()
}).strict();
/**
* One Layer-A pool's editable sizing draft, PER-ZONE spelling. `.strict()` — ONLY the two bounds
* below. `keepWarm`/`diskSizeGb` are read-only-derived (§13.1, RW2-6-3) and are REJECTED, not
* silently ignored; there is no `spot` (DP-wide). The `.refine()` re-imposes the min <= max
* invariant that `nodePoolAutoscaleSchema` (`dp-sizing.ts:68`) enforces on the sibling
* direct-sizing door — the delegated `ProvisioningService.update()` does NOT re-parse the sizing it
* is handed, so without this refine an inverted range would be persisted to `dp_explicit_sizing`
* and rendered into an invalid autoscaler range (round-2 security CR-1 / RW2-2-2).
*
* ## ⛔⛔ BOTH BOUNDS ARE **PER ZONE**. THEY USED TO BE CALLED `minNodes`/`maxNodes` AND SAID SO
* NOWHERE, AND THAT SILENCE PRODUCED A 3x ERROR ON PRODUCTION.
*
* Follow the value to GKE. This draft's `maxNodesPerZone` is written to the DataPlane XR as
* `spec.<pool>.autoscale.maxNodes` (`provisioning.service.ts` `setComputeView` ->
* `ProvisioningService.update`), and the Crossplane composition renders that verbatim into the
* NodePool MR as **`autoscaling[0].maxNodeCount`**
* (`zion-cp-compositions/fragments/_base-node-pools.gotmpl:85-86` for infra and its `default-app`
* twin below it). **`maxNodeCount` is a GKE PER-ZONE bound.** So the real ceiling is this number
* MULTIPLIED BY the pool's configured zone count, and nothing between here and GKE says the word
* "zone".
*
* ⛔ MEASURED 2026-08-31, on production, by the lead who caused it. `default-app-e2-standard-4`
* carried `maxNodeCount: 3` across `nodeLocations` of 3 — an effective ceiling of **9**. The lead
* raised that value to **5** intending a ceiling of 5 and produced **15**.
*
* ⛔ WHY THE UNIT IS IN THE NAME AND NOT IN THIS DOCBLOCK ALONE (C-014). No amount of documentation
* at the consumer fixes a field whose unit is not in its name: the name is what a consumer types,
* and the one thing that was already correct here — the console's `Max nodes per pool, per zone`
* label at `DataPlaneCapacityTab.tsx:275` — is precisely why nobody noticed the schema was silent.
* A correct label on one consumer is not protection for the next one.
*
* ⚠️ ONE UNIT FOR BOTH KEYS, and that is what makes a rename sufficient here. This schema is reached
* only through `dpComputePoolsPatchSchema`, whose keys are `infra` and `default-app` — both BASE
* pools, both per-zone. It is NOT the two-unit hazard C-014 rules on: an ISOLATED pool's bounds are
* cluster-wide totals under different GKE keys (`totalMaxNodeCount`), they never travel through this
* schema, and they get their own distinct names on {@link poolAutoscalerBoundsSchema}'s
* `cluster-total` arm.
* ⛔ Do NOT "unify" this schema with an isolated pool's bounds. The source fails CLOSED today — code
* reading `autoscaling.maxNodeCount` on an isolated pool gets `undefined` rather than a wrong number
* — and normalising the two converts a fail-closed absence into a fail-wrong number.
*
* ⚠️ NOT A CEILING. `maxNodesPerZone` alone is never the answer to "what is this pool's maximum".
* The effective ceiling is {@link effectiveMaxNodesTotal} over {@link dpComputeViewSchema}'s
* `poolAutoscalerBounds`, which needs the zone count — and the zone count is NOT on the DP's wire
* report (#1622, open). A consumer that renders this number as "the maximum" reproduces the
* incident above.
*/
const perZonePoolLimitsDraftSchema = z.object({
	/**
	* ⛔ PER ZONE. Lowering this can DRAIN AND EVICT LIVE NODES — it is the only node-pool write
	* that takes capacity away from running pods, and it is fenced out of the MCP surface for that
	* reason. It remains fully writable here because the console's sizing form legitimately sets it.
	*/
	minNodesPerZone: z.number().int().min(0).max(100),
	/** ⛔ PER ZONE. See the class docblock: `3` across 3 zones is a ceiling of 9, not 3. */
	maxNodesPerZone: z.number().int().min(1).max(100)
}).strict().refine((d) => d.minNodesPerZone <= d.maxNodesPerZone, {
	message: "minNodesPerZone must be <= maxNodesPerZone",
	path: ["minNodesPerZone"]
});
/**
* ⛔⛔ THE PRE-RENAME SPELLING, ACCEPTED FOR **ONE RELEASE CYCLE** AND THEN DELETED. C-072.
*
* @deprecated Send {@link perZonePoolLimitsDraftSchema}'s members. This arm exists only to survive
* one deploy, and {@link normalisePoolLimits}'s docblock carries the removal trigger.
*
* ## ⛔ WHY A UNION RATHER THAN AN ATOMIC RENAME: YOU CANNOT DEPLOY A BROWSER
*
* `dpComputePatchSchema` reaches this schema and is `@Body({schema})`-bound on **two separately
* deployable controllers** (`site-admin-api/src/modules/compute/compute.controller.ts`,
* `provisioning-svc/src/modules/provisioning/provisioning.controller.ts`) and re-parsed a third time
* in `ProvisioningService.setComputeView`. The body crosses the hop verbatim, all three ship as
* separate images from one `CP_SERVICES` array, and **the producer is a browser SPA**. A full
* `bin/release-cp` aligns the three images; it cannot align a CACHED SPA BUNDLE. Both members are
* required and the object is `.strict()`, so without this arm every skew combination 400s the
* console's capacity editor with `unrecognized_keys` — for as long as a user's tab stays open,
* which is unbounded.
*
* ⛔ `FleetCapacityPane.tsx`'s "Raise pool max" lever is a LIVE FLEET WRITE that constructs this
* body directly, so an already-loaded console is exactly the un-migrated caller this arm admits.
*
* ## ⛔ THE REMOVAL IS GATED ON THE RENAME BEING COMPLETE, NOT ON A DATE OR A RELEASE COUNT
*
* A dual-accept window whose removal is not gated on the rename completing is a deferred outage
* with a timer on it. See {@link normalisePoolLimits} for the trigger and the deletion checklist.
*
* ## ⛔ IT IS A PLAIN `.or()` MEMBER AND CARRIES **NO** `.transform()`. MEASURED (C-173)
*
* The obvious shape — normalising to the new names inside this arm with `.transform()` — **breaks
* JSON-Schema emission**: `z.toJSONSchema(schema, {target: 'openapi-3.1'})` throws
* `Transforms cannot be represented in JSON Schema`, and `createOpenApiDocument` runs exactly that
* call over every `@Body`-bound schema. That is SYS-074 — an `@Body`-bound schema that breaks
* emission at boot — so the normalisation lives in {@link normalisePoolLimits} instead, downstream
* of the parse. ⛔ Do not "simplify" this by moving it back into the schema.
*/
const legacyPoolLimitsDraftSchema = z.object({
	/** @deprecated PER ZONE. Renamed to `minNodesPerZone`; accepted for one release cycle. */
	minNodes: z.number().int().min(0).max(100),
	/** @deprecated PER ZONE. Renamed to `maxNodesPerZone`; accepted for one release cycle. */
	maxNodes: z.number().int().min(1).max(100)
}).strict().refine((d) => d.minNodes <= d.maxNodes, {
	message: "minNodes must be <= maxNodes",
	path: ["minNodes"]
});
/**
* One Layer-A pool's editable sizing draft: the per-zone spelling, OR the deprecated pre-rename one.
*
* ⛔ THE UNION IS THE DUAL-ACCEPT WINDOW (C-072/C-173). Read
* {@link legacyPoolLimitsDraftSchema}'s docblock for why it exists and
* {@link normalisePoolLimits}'s for when it goes.
*
* ⛔⛔ MIXED SPELLINGS ARE REFUSED, and that is a property worth knowing rather than assuming.
* MEASURED on zod 4.4.3: `{minNodesPerZone: 1, maxNodes: 3}` and a body carrying all four members
* both fail with `invalid_union`, because each arm is `.strict()` and neither accepts the other's
* keys. **Dual-accept is not key-salad-accept.**
*
* ⛔⛔ AND A NEGATIVE ASSERTION AGAINST THIS SCHEMA IS WEAKER THAN IT LOOKS (C-105). A body that
* fails the arm under test is retried against the other arm — where the members are ABSENT — so it
* fails there too and `success === false` still holds. **The rejection code changes from
* `unrecognized_keys` to `invalid_union`, and a test asserting only `success === false` goes green
* while its subject disappears.** ⇒ A test whose subject is a CONSTRAINT (`.min(1)`, the refine,
* `.strict()`) must assert against the ARM, by issue code, not merely that the union refused.
*/
const poolLimitsDraftSchema = perZonePoolLimitsDraftSchema.or(legacyPoolLimitsDraftSchema);
/**
* ⛔ THE ONE PLACE THE DUAL-ACCEPT WINDOW IS COLLAPSED. C-173.
*
* Both spellings mean the same two PER-ZONE numbers, so every reader of a parsed
* {@link poolLimitsDraftSchema} calls this and then sees exactly one shape. ⛔ Do NOT narrow the
* union inline at a call site: a union narrowed inline is narrowed wherever it is read, and "one
* reader today" is how a second declaration gets minted.
*
* ⛔⛔ **THIS FUNCTION IS WHERE THE DEADLINE LIVES, AND THAT IS ITS THIRD AND MAIN REASON FOR
* EXISTING.** C-072 grants dual-accept for ONE release cycle. A named export carrying the
* obligation is findable by grep; a branch inside a service method is not, and **dual-accept that
* nobody removes is permanent.**
*
* ## ⛔ THE REMOVAL TRIGGER — a condition, never a date and never a release count
*
* Delete {@link legacyPoolLimitsDraftSchema}, collapse {@link poolLimitsDraftSchema} back to
* {@link perZonePoolLimitsDraftSchema}, and delete this function, WHEN every producer of a
* `DpComputePatch` body carries the per-zone spelling. Enumerate them the way the rename was
* enumerated — by the files referencing `DpComputePatch` / `dpComputePatchSchema` /
* `poolSizingPatch` / `patchDpCompute`, then reading each site — never by a single grep pattern,
* which is what under-counted the set by 17 the first time. ⚠️ A cached browser bundle is a
* producer: the trigger is satisfied one full release AFTER the console ships the new spelling, not
* when the code lands.
*
* ⚠️ When the arm goes, the arms that pin the OLD spelling as accepted invert back to asserting a
* refusal. They are TEST-S-4 in this package's `dp-compute-view.test.ts` and TEST-A-5 in
* `provisioning-svc`'s `provisioning.controller.spec.ts`. ⛔ Invert them; do not delete them.
*/
function normalisePoolLimits(draft) {
	return "minNodesPerZone" in draft ? {
		minNodesPerZone: draft.minNodesPerZone,
		maxNodesPerZone: draft.maxNodesPerZone
	} : {
		minNodesPerZone: draft.minNodes,
		maxNodesPerZone: draft.maxNodes
	};
}
/**
* The PATCH `pools` object — Layer-A keys ONLY, each optional (DEC-COMPUTE-WIRE: "pools: [Layer-A sizing
* edits — infra/default-app ONLY]"). Declared as an explicit `.strict()` object rather than
* `z.record(layerAPoolKeySchema, …)` because in zod 4 a record keyed by an enum requires EVERY enum key to
* be present (exhaustive) — which would force a caller editing only `infra` to also send `default-app`.
* An explicit object also removes the pool-key string surface entirely: no pattern, no path-injection
* reachable from a key (the previous `demand:<nodeType>` key form is gone with the per-pool cap).
*/
const dpComputePoolsPatchSchema = z.object({
	infra: poolLimitsDraftSchema.optional(),
	"default-app": poolLimitsDraftSchema.optional()
}).strict();
/**
* PATCH /compute body — the CANONICAL wire WS-6 imports (DEC-COMPUTE-WIRE). `.strict()` (mass-assignment
* defense). `pools` = Layer-A sizing edits (may be `{}`); `limits` = the DP-wide `Partial<DpAdminLimits>`
* block with **REPLACE semantics** (an omitted sub-field CLEARS that bound — same contract as the direct
* `PATCH /v1/data-planes/:id { limits }` door, §2.1). `limits` OMITTED ENTIRELY ⇒ the stored limits row is
* left untouched (§13.0 / §13.3). There is NO demand/dedicated pool key and NO per-pool `spot`.
*
* ⛔ `pools[*]` bounds are PER ZONE and their names say so ({@link perZonePoolLimitsDraftSchema}). The
* effective ceiling is `maxNodesPerZone * configuredZoneCount`; this body never carries a total.
*/
const dpComputePatchSchema = z.object({
	pools: dpComputePoolsPatchSchema,
	limits: dpAdminLimitsSchema.optional()
}).strict();
/**
* compute-classes D9/D16 — the Layer-C override PATCH body.
*
* ⛔ THREE STATES PER HALF, and the distinction is the whole point:
*   • key ABSENT        ⇒ leave that half of the override exactly as stored (a true PATCH).
*   • key present, null ⇒ CLEAR that half (fall back to the manifest class / the tenant size).
*   • key present, value⇒ set it.
* Clearing BOTH halves deletes the row, which is the "revert to defaults" door D9 requires and which
* D16's two-route pair has no DELETE for (D34: no third route).
*
* ⛔ `{}` IS REFUSED. An empty body would stamp `updated_by`/`updated_at` for a non-event and read to
* the caller as a successful change. Same shape and rationale as `updateDcrLifecycleSettingsSchema`
* (`cp-auth-api/.../dcr-lifecycle-settings.dto.ts:45`), which is bound via `@Body({ schema })` today
* — so the `.refine` survives `deepStrictSchema` (zod-4 checks `.check()`-clone the same object;
* `deep-strict-schema.ts:16`).
*
* ⛔ THIS (the WIRE, nullable) and `AppComputeOverride` (the ENVELOPE, `{class?, size?}`, in
* `@zion/sdk-cp-dp-contracts`) are DIFFERENT TYPES and must stay so. The envelope never carries
* `null` — a cleared half is an ABSENT KEY there, because the operator's "no override for this half"
* branch keys on absence. Conflating them puts a `null` on the DP wire the operator would have to
* special-case, and the two live in different packages precisely so the compiler keeps them apart.
*/
const appComputeOverridePatchSchema = z.object({
	class: computeClassSchema.nullable().optional(),
	size: sizeSchema.nullable().optional()
}).strict().refine((v) => v.class !== void 0 || v.size !== void 0, { message: "provide `class` and/or `size`; use an explicit null to clear a half" });
//#endregion
export { COMPUTE_VIEW_DEFAULT_DISK_GIB, COMPUTE_VIEW_DISK_GIB_MAX, appComputeOverridePatchSchema, dpComputePatchSchema, dpComputePoolsPatchSchema, dpComputeViewSchema, effectiveMaxNodesTotal, layerAPoolKeySchema, legacyPoolLimitsDraftSchema, liveNodeTotalsSchema, nodePoolBoundsEntrySchema, normalisePoolLimits, perZonePoolLimitsDraftSchema, poolAdvisorySchema, poolAutoscalerBoundsSchema, poolComputeRoleSchema, poolComputeViewSchema, poolLimitsDraftSchema };

//# sourceMappingURL=dp-compute-view.js.map